import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout';
import Icone from '../components/Icones';
import { api, EmpresaVinculo } from '../lib/api';
import { useAuth } from '../lib/auth';
import { rotuloPerfil } from '../lib/rotulos';
import { ID_GA4, installGoogleTag, trackLogin } from '../lib/gtm';

const MANTER_CONECTADO_KEY = 'lizzi_manter_conectado';

export default function Login() {
  const { login, selecionarEmpresa, logout, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [senhaReset, setSenhaReset] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [mostrarSenhaReset, setMostrarSenhaReset] = useState(false);
  const [erro, setErro] = useState('');
  const [msg, setMsg] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [escolha, setEscolha] = useState<EmpresaVinculo[] | null>(null);
  const [modoReset, setModoReset] = useState(false);
  const [emailResetVerificado, setEmailResetVerificado] = useState('');
  const [manterConectado, setManterConectado] = useState(() => localStorage.getItem(MANTER_CONECTADO_KEY) !== 'false');

  // O GA4 so e montado no app logado (components/Layout.tsx) - a tela de login e
  // publica. Como o evento 'login' nasce AQUI, a tag e configurada antes de emiti-lo:
  // sem isso o evento sairia antes do config e nao cairia na propriedade.
  // A chamada e idempotente, entao o Layout nao reconfigura nada depois.
  function marcarLoginNoAnalytics() {
    installGoogleTag(ID_GA4, { enviarPageView: false });
    trackLogin('senha');
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    setCarregando(true);
    try {
      localStorage.setItem(MANTER_CONECTADO_KEY, manterConectado ? 'true' : 'false');
      const r = await login(email.trim(), senha, manterConectado);
      if (r.precisaEscolher) {
        setEscolha(r.empresas);
        return;
      }
      marcarLoginNoAnalytics();
      nav(r.usuario?.perfil === 'admin_geral' ? '/app/empresas' : '/app');
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha no login');
    } finally {
      setCarregando(false);
    }
  }

  async function escolher(empresaId: number) {
    setErro('');
    setMsg('');
    setCarregando(true);
    try {
      await selecionarEmpresa(empresaId);
      marcarLoginNoAnalytics();
      nav('/app');
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao selecionar empresa');
    } finally {
      setCarregando(false);
    }
  }

  async function verificarEmailReset() {
    setErro('');
    setMsg('');
    setCarregando(true);
    try {
      const data = await api<{ email: string; nome: string }>('/senha_reset_rapido.php', {
        method: 'POST',
        body: { acao: 'verificar', email: email.trim() },
      });
      setEmailResetVerificado(data.email);
      setMsg(`E-mail localizado para ${data.nome}. Agora defina a nova senha.`);
    } catch (err) {
      setEmailResetVerificado('');
      setErro(err instanceof Error ? err.message : 'Falha ao verificar o e-mail');
    } finally {
      setCarregando(false);
    }
  }

  async function redefinirSenha(e: FormEvent) {
    e.preventDefault();
    setErro('');
    setMsg('');
    setCarregando(true);
    try {
      await api('/senha_reset_rapido.php', {
        method: 'POST',
        body: { acao: 'redefinir', email: email.trim(), senha: senhaReset },
      });
      setSenha('');
      setSenhaReset('');
      setEmailResetVerificado('');
      setModoReset(false);
      setMsg('Senha restaurada com sucesso. Agora e so entrar normalmente.');
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao restaurar a senha');
    } finally {
      setCarregando(false);
    }
  }

  function abrirReset() {
    setModoReset(true);
    setErro('');
    setMsg('');
    setSenha('');
    setSenhaReset('');
    setMostrarSenha(false);
    setMostrarSenhaReset(false);
    setEmailResetVerificado('');
  }

  function fecharReset() {
    setModoReset(false);
    setErro('');
    setMsg('');
    setSenhaReset('');
    setMostrarSenhaReset(false);
    setEmailResetVerificado('');
  }

  // Sessao com "Manter conectado" (token sem expiracao - sempre ligado para
  // tecnico) ja fica salva no localStorage. Sem isso, quem cai de novo em
  // /login via um link ou favorito precisava digitar e-mail/senha de novo
  // mesmo sem ter saido - a tela so mostrava o formulario em branco.
  function continuarLogado() {
    if (!user) return;
    nav(user.perfil === 'admin_geral' ? '/app/empresas' : '/app');
  }

  async function trocarConta() {
    setErro('');
    setCarregando(true);
    try {
      await logout();
    } finally {
      setCarregando(false);
    }
  }

  if (user && !escolha) {
    return (
      <AuthLayout>
        <h2 style={{ color: 'var(--azul)' }}>Olá, {user.nome}!</h2>
        <p style={{ color: 'var(--texto-suave)', marginTop: 0 }}>
          Você continua conectado{user.empresa_nome ? ' em ' + user.empresa_nome : ''}.
        </p>
        {erro && <div className="msg-erro">{erro}</div>}
        <button className="btn lg" style={{ width: '100%', marginTop: 8 }} onClick={continuarLogado}>
          Entrar
        </button>
        <p style={{ marginTop: 16, fontSize: 14 }}>
          <button type="button" className="lp-link" disabled={carregando} onClick={trocarConta}>
            {carregando ? 'Saindo...' : 'Não é você? Sair e usar outra conta'}
          </button>
        </p>
      </AuthLayout>
    );
  }

  if (escolha) {
    return (
      <AuthLayout>
        <h2 style={{ color: 'var(--azul)' }}>Escolha a empresa</h2>
        <p style={{ color: 'var(--texto-suave)', marginTop: 0 }}>
          Sua conta tem acesso a mais de uma empresa. Selecione com qual quer entrar.
        </p>
        {erro && <div className="msg-erro">{erro}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 8 }}>
          {escolha.map((emp) => (
            <button
              key={emp.empresa_id}
              type="button"
              className="btn sec"
              style={{ justifyContent: 'space-between', padding: '14px 16px', textAlign: 'left' }}
              disabled={carregando}
              onClick={() => escolher(emp.empresa_id)}
            >
              <span style={{ fontWeight: 800 }}>{emp.empresa_nome}</span>
              <span className="badge">{rotuloPerfil(emp.perfil)}</span>
            </button>
          ))}
        </div>
        <p style={{ marginTop: 16, fontSize: 14 }}>
          <button type="button" className="lp-link" onClick={() => { setEscolha(null); setSenha(''); }}>
            Voltar e usar outra conta
          </button>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 style={{ color: 'var(--azul)' }}>{modoReset ? 'Restaurar senha' : 'Bem-vindo de volta'}</h2>
      <p style={{ color: 'var(--texto-suave)', marginTop: 0 }}>
        {modoReset
          ? 'Digite seu e-mail, confirme que ele existe e defina a nova senha.'
          : 'Entre na sua conta para continuar.'}
      </p>
      {erro && <div className="msg-erro">{erro}</div>}
      {msg && <div className="msg-ok">{msg}</div>}

      {!modoReset ? (
        <form onSubmit={enviar}>
          <label>E-mail</label>
          <div className="campo">
            <span className="campo-ic"><Icone nome="email" size={18} /></span>
            <input className="input com-ic" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          </div>
          <label>Senha</label>
          <div className="campo">
            <span className="campo-ic"><Icone nome="cadeado" size={18} /></span>
            <input className="input com-ic com-acao" type={mostrarSenha ? 'text' : 'password'} value={senha} onChange={(e) => setSenha(e.target.value)} required />
            <button type="button" className="campo-acao" onClick={() => setMostrarSenha((v) => !v)}>
              {mostrarSenha ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          <label className="remember-row">
            <input
              type="checkbox"
              checked={manterConectado}
              onChange={(e) => setManterConectado(e.target.checked)}
            />
            <span>
              <strong>Manter conectado</strong>
              <small>Ideal para quem usa o sistema todos os dias neste dispositivo.</small>
            </span>
          </label>
          <button className="btn lg" style={{ width: '100%', marginTop: 18 }} disabled={carregando}>
            {carregando && <span className="btn-spinner" />} {carregando ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      ) : (
        <form onSubmit={redefinirSenha}>
          <label>E-mail</label>
          <div className="campo">
            <span className="campo-ic"><Icone nome="email" size={18} /></span>
            <input
              className="input com-ic"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailResetVerificado('');
                setMsg('');
              }}
              required
              autoFocus
            />
          </div>

          {!emailResetVerificado ? (
            <button
              type="button"
              className="btn sec"
              style={{ width: '100%', marginTop: 18 }}
              disabled={carregando}
              onClick={verificarEmailReset}
            >
              {carregando && <span className="btn-spinner" />} {carregando ? 'Verificando...' : 'Verificar e-mail'}
            </button>
          ) : (
            <>
              <label>Nova senha</label>
              <div className="campo">
                <span className="campo-ic"><Icone nome="cadeado" size={18} /></span>
                <input
                  className="input com-ic com-acao"
                  type={mostrarSenhaReset ? 'text' : 'password'}
                  value={senhaReset}
                  onChange={(e) => setSenhaReset(e.target.value)}
                  minLength={6}
                  required
                />
                <button type="button" className="campo-acao" onClick={() => setMostrarSenhaReset((v) => !v)}>
                  {mostrarSenhaReset ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
              <button className="btn lg" style={{ width: '100%', marginTop: 18 }} disabled={carregando}>
                {carregando && <span className="btn-spinner" />} {carregando ? 'Restaurando...' : 'Restaurar senha'}
              </button>
            </>
          )}
        </form>
      )}

      <p style={{ marginTop: 16, fontSize: 14 }}>
        <button type="button" className="lp-link" onClick={modoReset ? fecharReset : abrirReset}>
          {modoReset ? 'Voltar para o login' : 'Esqueci minha senha'}
        </button>
      </p>

      {!modoReset && (
        <p style={{ marginTop: 16, fontSize: 14, color: 'var(--texto-suave)' }}>
          Nao tem conta? <Link to="/cadastro">Criar conta gratis</Link>
        </p>
      )}
    </AuthLayout>
  );
}
